/**
 * The shareholder record.
 *
 * Same shape and stylesheet as the partner, ambassador and task editors. Two
 * things make it its own screen: holdings are lists you add rows to rather than
 * fields you type into, and both totals are shown live because a share register
 * that makes you add up its own rows is a share register nobody trusts.
 *
 * ## A typed row is not thrown away
 *
 * The first shareholder saved through this screen lost its shares: they had been
 * typed into the compose row and Save was pressed without Add, so the holding
 * was discarded in silence and the record read "Potential investor" beside a
 * number somebody had just entered. Save now folds a complete compose row in,
 * and refuses a half-filled one rather than dropping it.
 *
 * ## Exercising an option is a share capital increase, and is booked as one
 *
 * Decision 16 of the 14 Aug round, built after Bjarte asked for the designer's
 * model over the status flip that shipped first. Exercising takes three inputs
 * before it records anything — the increase it is booked against, the board or
 * general-meeting resolution that allowed it, and the date — and it does **not**
 * create shares. That happens on a second, separate act: *Strike price paid*.
 * Between the two the grant reads "Exercised — unpaid", sits on the record in
 * full, and counts in no total anywhere in the app.
 *
 * The order is the whole point. Subscribed-and-unpaid shares are not shares;
 * counting them would overstate the company's capital and dilute every real
 * holder against shares nobody has paid for.
 *
 * Everything here is an edit to the draft and takes effect on Save, like every
 * other control in this dialog — so Cancel means one thing. The one thing that
 * reaches Firestore ahead of the record is a *new* increase, created on the way
 * out so the issuance exists before a grant points at it; the transfer form
 * creates an unknown buyer the same way.
 */
import { useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import { useShareholderLinks, type LinkKind } from './useShareholderLinks'
import { fmtKrExact, fmtKrPrice, parseKr } from '@/shared/util/money'
import { formatDate, todayIso } from '@/shared/util/dates'
import {
  activeOptions,
  EMPTY_SHAREHOLDER,
  exerciseGapNote,
  grantTermsNote,
  holderStatus,
  holderStatusStyle,
  isUnpaidExercise,
  splitGrant,
  MAIL_STATES,
  netShares,
  storedPersonnummer,
  optionSplit,
  optionStateLabel,
  type Attachment,
  type Issuance,
  type OptionGrant,
  type SharePurchase,
  type Subscription,
  type ShareholderMail,
  type ShareTransfer,
  type Shareholder,
} from '@/types/ownership'
import type { CompanyDetails } from '@/types/company'
import {
  buildCertificate,
  registeredShares,
  buildReceipt,
  certifiableTransactions,
  paidTransactions,
} from '@/types/shareDocuments'
import { AttachmentsField } from './AttachmentsField'
import { ShareReceipt } from './ShareReceipt'
import { ShareCertificate } from './ShareCertificate'
import '@/shared/ui/RecordForm.css'
import './Ownership.css'

/**
 * Stands in for the increase an exercise will be booked against, until Save.
 *
 * The same shape as the transfer form's new buyer: nothing is written while the
 * dialog is open, so Cancel keeps meaning one thing, and the issuance is created
 * on the way to saving the record that needs it.
 */
const NEW_INCREASE = '__new-increase__'

interface Props {
  holder: Shareholder
  isNew: boolean
  issuances: Issuance[]
  /** For the receipt: who received the money, and the pålydende it is measured from. */
  company: CompanyDetails
  /** The whole ledger; this record shows the rows naming it. */
  transfers: ShareTransfer[]
  /** What has been sent to this holder, newest first. */
  mail: ShareholderMail[]
  /** Resolves the other side of a transfer to a name. */
  holderName: (id: string) => string
  /**
   * Creates the share capital increase an exercise is booked against, when the
   * grant is being exercised into a new one rather than an existing round.
   */
  onCreateIncrease: (name: string, date: string, pricePerShare: number | null) => Promise<string>
  onClose: () => void
  onSave: (draft: Omit<Shareholder, 'id' | 'createdAt' | 'updatedAt'>, id?: string) => Promise<void>
  onDelete: (id: string) => Promise<void>
}

const nb = (n: number) => n.toLocaleString('nb-NO')
const today = todayIso

export function ShareholderModal({
  holder,
  isNew,
  issuances,
  company,
  transfers,
  mail,
  holderName,
  onCreateIncrease,
  onClose,
  onSave,
  onDelete,
}: Props) {
  const [form, setForm] = useState<Shareholder>(holder)
  /** Already filed by the time it is listed — the upload happens on pick. */
  const [attachments, setAttachments] = useState<Attachment[]>(holder.attachments ?? [])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The row being composed, kept apart from the saved list so a half-typed
  // purchase is never a holding.
  const [buy, setBuy] = useState({ issuanceId: '', shares: '', date: '', price: '', note: '' })
  const [grant, setGrant] = useState({
    count: '',
    strikePrice: '',
    grantDate: '',
    expiryDate: '',
  })
  /**
   * Which record a *person* is also in. A company has only one option — the lead
   * — so it needs no second control, and the picker asks the holder kind first.
   */
  const [personLink, setPersonLink] = useState<LinkKind>(
    holder.linkedTeamEmail
      ? 'team'
      : holder.linkedAmbassadorId
        ? 'ambassador'
        : holder.linkedLeadContactId
          ? 'contact'
          : 'none',
  )
  const { targets, loading: targetsLoading } = useShareholderLinks(
    form.kind === 'company' ? 'lead' : personLink,
  )
  /** The contact on the linked lead, for recognition beside a company's link. */
  const primaryContact = form.kind === 'company' ? form.linkedLeadContact : ''

  /** Index of the grant whose exercise is being booked, or null. */
  /**
   * The `PaidTransaction.key` whose receipt is open, or null.
   *
   * Built from `holder` — the **saved** record — and never from `form`. These
   * are documents about facts: issuing one off an unsaved draft would put a date
   * and a kurs on paper for a payment that Cancel is still able to undo. So
   * marking a row paid gives it a receipt after Save, not before.
   */
  const [openDoc, setOpenDoc] = useState<{ kind: 'receipt' | 'certificate'; key: string } | null>(
    null,
  )
  const paid = new Map(paidTransactions(holder, issuances).map((t) => [t.key, t] as const))
  /**
   * The acquisitions this holder can be certified for — a subset of `paid`,
   * because both documents need the money to have arrived and the aksjeeierbevis
   * additionally needs the round reported to Foretaksregisteret.
   */
  const certifiable = new Map(
    certifiableTransactions(holder, issuances).map((t) => [t.key, t] as const),
  )
  const openTx = openDoc
    ? (openDoc.kind === 'receipt' ? paid : certifiable).get(openDoc.key)
    : undefined

  const [booking, setBooking] = useState<number | null>(null)
  /** `count` is a string while typed; '' falls back to the whole grant. */
  const [book, setBook] = useState({ issuanceId: '', resolution: '', date: '', count: '' })
  /**
   * A refusal beside the control that refused it — F2, and 56's principle that
   * a refusal never fails silent.
   *
   * The 24 Aug run reported the two option-exercise refusals as *gone* and Book
   * as silently inert. They were never gone: `bookExercise` sets the message and
   * always did. The single `error` slot is the **last element in this dialog**,
   * after Options and the attachments field, and the Book form sits half a
   * scroll above it — so the message appeared where nobody pressing the button
   * could see it. A refusal rendered off-screen is a refusal that failed silent,
   * whatever the code says. The buy-back refusal read as working for exactly
   * this reason: its dialog is short enough that the bottom is on screen.
   *
   * So a row-level refusal is rendered where it happened. `error` keeps the
   * dialog-level problems — a save that would not go through.
   */
  const [rowError, setRowError] = useState<{ where: 'buy' | 'grant' | 'book'; text: string } | null>(
    null,
  )
  const refuse = (where: 'buy' | 'grant' | 'book', text: string) => setRowError({ where, text })
  const rowErrorFor = (where: 'buy' | 'grant' | 'book') =>
    rowError?.where === where ? rowError.text : null

  const set = (patch: Partial<Shareholder>) => setForm((f) => ({ ...f, ...patch }))
  const issuanceName = (id: string) =>
    id === NEW_INCREASE
      ? 'a new increase, created on save'
      : issuances.find((i) => i.id === id)?.name ?? 'Unknown round'

  /**
   * The compose row as a holding, or why it is not one.
   *
   * `{}` for an untouched row — nothing typed is nothing to save — and a message
   * for one that is started but incomplete. Used by Add and by Save alike, so
   * the two cannot disagree about what counts as a holding.
   */
  /**
   * Is the round this row names still open? — decision 76, and the F1 blocker.
   *
   * The 24 Aug run recorded "150 shares of interest" against an Ongoing round
   * and got a shareholder: the status pill flipped, shares outstanding went
   * 49 902 → 50 052, all 144 real holders were diluted, and the round card read
   * "Shares issued 150". Nothing downstream was wrong — `holdings`,
   * `sharesOutstanding` and `issuanceTotals` all read `purchases` and never
   * `subscriptions`, exactly as designed. **This form was the bug**: it wrote a
   * purchase whatever the round's status, so the interest really was a purchase
   * by the time anything else saw it.
   *
   * So the round decides which ledger the row goes into. The form cannot offer
   * "record a purchase in a round that has issued nothing" at all, because that
   * is not a thing the register can hold.
   */
  const buyRound = issuances.find((i) => i.id === buy.issuanceId)
  const buyIsInterest = buyRound?.status === 'ongoing'

  const pendingPurchase = (): { row?: SharePurchase; error?: string } => {
    if (
      !buy.issuanceId &&
      !buy.shares.trim() &&
      !buy.date &&
      !buy.price.trim() &&
      !buy.note.trim()
    )
      return {}
    const shares = Number(buy.shares)
    if (!buy.issuanceId) return { error: 'That purchase has no round. Pick one, or clear the row.' }
    if (!Number.isFinite(shares) || shares <= 0) {
      return { error: 'That purchase has no share count. Add one, or clear the row.' }
    }
    // A price only when one was agreed. Empty means the round's, which is the
    // ordinary case; writing the round's price onto the row would freeze it, so
    // correcting the round later would leave this holder on the old one.
    const agreed = buy.price.trim() === '' ? null : parseKr(buy.price)
    if (buy.price.trim() !== '' && (agreed === null || agreed <= 0)) {
      return { error: 'That agreed price is not a number.' }
    }
    const note = buy.note.trim()
    return {
      row: {
        issuanceId: buy.issuanceId,
        shares: Math.round(shares),
        pricePerShare: agreed,
        // The round's own date unless somebody says otherwise — a late
        // subscription is the exception, not the rule.
        date: buy.date || buyRound?.date || '',
        // Spread in, never `undefined` — see `mapPurchases`. What the source
        // said, where a holding came from something worth recording (78).
        ...(note ? { note } : {}),
      },
    }
  }

  /**
   * The same row, as interest in a round that has not closed — 76, 79, 82.
   *
   * A `Subscription` rather than a `SharePurchase`, and the difference is the
   * whole point: nothing here reaches a holding, a percentage or shares
   * outstanding, because it is not in the array those are summed from.
   *
   * The note is required rather than optional. Decision 82 renders it verbatim
   * and says why — "registered interest" and "signed subscription form, kurs kr
   * 30,50" are a maybe and a commitment, and a tegningsliste that flattened them
   * would lose the difference. An unlabelled promise is the one thing this list
   * must not contain.
   */
  const pendingSubscription = (): { row?: Subscription; error?: string } => {
    const { row, error: why } = pendingPurchase()
    if (!row) return { error: why }
    if (!buy.note.trim()) {
      return {
        error:
          'Interest needs the source’s own words — what was recorded, and where. ' +
          '“Registrert interesse 14.08.2026” and “Signert tegningsblankett, kurs kr 30,50” are ' +
          'different commitments, and the tegningsliste shows them as written.',
      }
    }
    return {
      row: {
        issuanceId: row.issuanceId,
        shares: row.shares,
        pricePerShare: row.pricePerShare,
        note: buy.note.trim(),
      },
    }
  }

  const pendingGrant = (): { row?: OptionGrant; error?: string } => {
    if (!grant.count.trim() && !grant.strikePrice.trim() && !grant.grantDate && !grant.expiryDate) {
      return {}
    }
    const n = Number(grant.count)
    if (!Number.isFinite(n) || n <= 0) {
      return { error: 'That grant has no option count. Add one, or clear the row.' }
    }
    // `parseKr`, not `Number`: a strike typed as `9,50` is a strike, and
    // `Number` reads it as NaN and drops it without saying so.
    const strike = grant.strikePrice.trim() === '' ? null : parseKr(grant.strikePrice)
    if (grant.strikePrice.trim() !== '' && strike === null) {
      return { error: 'That strike price is not a number.' }
    }
    return {
      row: {
        count: Math.round(n),
        grantDate: grant.grantDate,
        strikePrice: strike,
        expiryDate: grant.expiryDate,
        // A grant is outstanding the day it is written down. Nothing else is a
        // sensible starting state — the other two are things that happen to it.
        status: 'active',
        exerciseDate: '',
        expiredDate: '',
        exerciseIssuanceId: '',
        exerciseResolution: '',
        paidDate: '',
      },
    }
  }

  /** Patch one grant on the draft. See the module note. */
  const patchGrant = (i: number, patch: Partial<OptionGrant>) =>
    set({ options: form.options.map((o, x) => (x === i ? { ...o, ...patch } : o)) })

  /**
   * Book an exercise as the capital increase it is.
   *
   * Three things are written together because a registry would ask for all
   * three: which increase it belongs to, what allowed it, and when. What is
   * deliberately *not* written is a paid date — the shares do not exist until
   * the strike price lands, and that is a second, separate act.
   */
  const bookExercise = (i: number) => {
    if (!book.issuanceId) {
      refuse('book', 'An exercise has to be booked against a share capital increase.')
      return
    }
    if (!book.resolution.trim()) {
      refuse('book', 'An exercise needs the resolution that allowed it.')
      return
    }
    // Part of the agreement can be exercised: the grant is split, the booked
    // part stays where it is and the rest stays active as its own grant.
    const total = form.options[i]?.count ?? 0
    const count = book.count.trim() === '' ? total : Number(book.count)
    const options = splitGrant(form.options, i, count)
    if (!options) {
      refuse('book', `Options exercised has to be a whole number from 1 to ${nb(total)}.`)
      return
    }
    setRowError(null)
    set({
      options: options.map((o, x) =>
        x === i
          ? {
              ...o,
              status: 'exercised',
              exerciseDate: book.date || today(),
              expiredDate: '',
              exerciseIssuanceId: book.issuanceId,
              exerciseResolution: book.resolution.trim(),
              paidDate: '',
            }
          : o,
      ),
    })
    setBooking(null)
    setBook({ issuanceId: '', resolution: '', date: '', count: '' })
  }

  const letExpire = (i: number) =>
    patchGrant(i, {
      status: 'expired',
      expiredDate: today(),
      exerciseDate: '',
      exerciseIssuanceId: '',
      exerciseResolution: '',
      paidDate: '',
    })

  /** Back to outstanding — every trace of the settlement goes with it. */
  const reactivate = (i: number) =>
    patchGrant(i, {
      status: 'active',
      exerciseDate: '',
      expiredDate: '',
      exerciseIssuanceId: '',
      exerciseResolution: '',
      paidDate: '',
    })

  const addPurchase = () => {
    // An open round takes interest, never a purchase — see `buyIsInterest`.
    if (buyIsInterest) {
      const { row, error: why } = pendingSubscription()
      if (!row) {
        refuse('buy', why ?? 'Nothing to add yet.')
        return
      }
      setRowError(null)
      set({ subscriptions: [...(form.subscriptions ?? []), row] })
      setBuy({ issuanceId: '', shares: '', date: '', price: '', note: '' })
      return
    }
    const { row, error: why } = pendingPurchase()
    if (!row) {
      refuse('buy', why ?? 'Nothing to add yet.')
      return
    }
    setRowError(null)
    set({ purchases: [...form.purchases, row] })
    setBuy({ issuanceId: '', shares: '', date: '', price: '', note: '' })
  }

  const addGrant = () => {
    const { row, error: why } = pendingGrant()
    if (!row) {
      refuse('grant', why ?? 'Nothing to add yet.')
      return
    }
    setRowError(null)
    set({ options: [...form.options, row] })
    setGrant({ count: '', strikePrice: '', grantDate: '', expiryDate: '' })
  }

  /** This holder's own rows in the transfer ledger, newest first. */
  /**
   * This holder's transfers, oldest first — off-script 16.
   *
   * They arrived in the collection's own order, which is newest-first, so a
   * record read top to bottom told the holding's story backwards. Sorted on read
   * for the same reason issuances are (18): the order is a fact about the
   * shares, not about when the rows were written.
   */
  const mine = transfers
    .filter((t) => t.fromId === holder.id || t.toId === holder.id)
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date))
  const held = netShares(form, transfers)
  const status = holderStatusStyle(holderStatus(form, transfers))
  const grants = optionSplit(form)
  // An exercise that has been booked is a capital event on the record, paid or
  // not, so it holds the record on the register the same way a purchase does.
  const deletable =
    mine.length === 0 &&
    form.purchases.length === 0 &&
    grants.exercised === 0 &&
    grants.unpaid === 0

  const save = async () => {
    if (!form.name.trim()) {
      setError('A shareholder needs a name.')
      return
    }
    /**
     * No length check on the personnummer — 29 Aug 2026.
     *
     * There was one, and it refused anything that was not eleven digits. The
     * register holds fourteen foreign holders whose identifier is not:
     * `FDRGST68M22H501Z`, `759-229-578 (Canada)`, `030476-0734`. So the check
     * made those records unsaveable — the same shape of bug as the geo field,
     * where a rule about the ordinary case blocked an edit to something else
     * entirely.
     *
     * `mapShareholder` has always kept a non-conforming value exactly as the
     * source wrote it, and said why. This form was arguing with its own
     * repository. Anything is accepted now and stored verbatim; the eye does the
     * checking, which is what the format helpers already assumed.
     */
    // Whatever is in the compose rows belongs to the record being saved.
    const buyPending = pendingPurchase()
    const grantPending = pendingGrant()
    if (buyPending.error || grantPending.error) {
      setError(buyPending.error ?? grantPending.error!)
      return
    }
    /**
     * There is deliberately no check here for a half-written exercise.
     *
     * There was, and it made eight holders unsaveable: `closeIssuance` exercised
     * their option-programme grants on 19 Aug without a board reference, and
     * from that day changing an entry date — or anything else — failed on a
     * grant the person had not touched.
     *
     * It could never have caught a live mistake either. `bookExercise` refuses
     * without both the increase and the resolution, and Undo returns the grant
     * to `active` and clears them, so this shape cannot be produced here. The
     * check only ever fired on data that arrived from a script, and blocking an
     * unrelated edit is the wrong answer to that — decision 80 already settles
     * what the right one is. `exerciseGapNote` states the gap on the row.
     */
    setSaving(true)
    setError(null)
    try {
      const { id: _id, createdAt: _c, updatedAt: _u, ...draft } = form
      void _id
      void _c
      void _u
      let options = grantPending.row ? [...draft.options, grantPending.row] : draft.options
      // A grant exercised into a new increase creates it on the way out, so the
      // issuance exists before anything points at it.
      if (options.some((o) => o.exerciseIssuanceId === NEW_INCREASE)) {
        const first = options.find((o) => o.exerciseIssuanceId === NEW_INCREASE)!
        const date = first.exerciseDate || today()
        const id = await onCreateIncrease(
          `Options exercised ${formatDate(date)}`,
          date,
          first.strikePrice,
        )
        options = options.map((o) =>
          o.exerciseIssuanceId === NEW_INCREASE ? { ...o, exerciseIssuanceId: id } : o,
        )
      }
      await onSave(
        {
          ...draft,
          name: draft.name.trim(),
          email: draft.email.trim().toLowerCase(),
          personnummer: storedPersonnummer(draft.personnummer),
          purchases: buyPending.row ? [...draft.purchases, buyPending.row] : draft.purchases,
          options,
          attachments,
        },
        isNew ? undefined : holder.id,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={isNew ? 'New shareholder' : form.name || 'Shareholder'}
      onClose={onClose}
      width={680}
      footer={
        <>
          {/* A holder who has held shares stays on the register. Bjarte's
              answer on retention was that the history is the point: a register
              that forgets who held shares last year is not a register. Somebody
              who never bought anything is a contact, and can go — unless a
              transfer names them, which would leave that row pointing at
              nobody.

              Where Delete would be, the reason stands instead. The 14 Aug round
              asks for it in those words: a missing button explains nothing, and
              this one has an answer. */}
          {!isNew &&
            (deletable ? (
              <button
                className="btn btn--ghost recform__delete"
                disabled={saving}
                onClick={() => void onDelete(holder.id)}
              >
                Delete shareholder
              </button>
            ) : (
              <p className="recform__note own__keep">
                Kept for the share register.
                {held > 0 &&
                  ' Record a transfer or a buy-back to end the holding.'}
              </p>
            ))}
          <button className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn--primary" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save shareholder'}
          </button>
        </>
      }
    >
      <div className="recform">
        <section className="recform__group">
          <h4 className="recform__legend">Details</h4>
          {/* Kind first, because the rest of the form reads it: the name label,
              which identifier is asked for, and what the record may be linked
              to. Decision 47 — both kinds are ordinary, and an individual is not
              a special case of a company or the other way round. */}
          <div className="own__tabs own__kind">
            <button
              className="chip"
              aria-pressed={form.kind === 'person'}
              onClick={() => set({ kind: 'person', orgNumber: '', linkedLeadId: '', linkedLabel: '' })}
            >
              Person
            </button>
            <button
              className="chip"
              aria-pressed={form.kind === 'company'}
              onClick={() =>
                set({
                  kind: 'company',
                  personnummer: '',
                  linkedLeadContactId: '',
                  linkedLeadContact: '',
                  linkedAmbassadorId: '',
                  linkedTeamEmail: '',
                  linkedLabel: '',
                })
              }
            >
              Company
            </button>
          </div>
          <div className="recform__grid">
            <label className="field recform__wide">
              <span>{form.kind === 'company' ? 'Legal name' : 'Name'}</span>
              <input
                className="input"
                value={form.name}
                onChange={(e) => set({ name: e.target.value })}
              />
            </label>
            <label className="field recform__wide">
              <span>Address</span>
              <input
                className="input"
                value={form.address}
                onChange={(e) => set({ address: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Email</span>
              <input
                className="input"
                value={form.email}
                onChange={(e) => set({ email: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Phone</span>
              <input
                className="input"
                value={form.phone}
                onChange={(e) => set({ phone: e.target.value })}
              />
            </label>
            {form.kind === 'company' ? (
              <label className="field recform__wide">
                <span>Organisasjonsnummer</span>
                <input
                  className="input own__pnr"
                  inputMode="numeric"
                  value={form.orgNumber}
                  placeholder="9 siffer"
                  onChange={(e) => set({ orgNumber: e.target.value })}
                />
                <span className="field__note">
                  Public information from Enhetsregisteret, so it is shown in full everywhere —
                  masking a public number would suggest the masking on a personnummer is
                  decoration.
                </span>
              </label>
            ) : (
              <label className="field recform__wide">
                <span>Personnummer</span>
                <input
                  className="input own__pnr"
                  inputMode="numeric"
                  value={form.personnummer}
                  placeholder="11 siffer, eller som landet skriver det"
                  onChange={(e) => set({ personnummer: e.target.value })}
                />
                <span className="field__note">
                  {/* Decision 100. The old wording said "masked to the last four
                      in the list", which promised a masked column somewhere —
                      and decision 14 keeps a personnummer out of every list view
                      at every width, masked or not. A masked column only ever
                      said "on file", which the record says better. Saying the
                      field is masked in a list teaches the reader that the list
                      shows it. */}
                  Shown in full only here and masked to the last four elsewhere on this record. It
                  is in no list view at any width, admin-only in the rules, and kept for former
                  holders. Any format — a foreign holder's identifier is stored exactly as their
                  country writes it.
                </span>
              </label>
            )}
          </div>
        </section>

        {/* Found rather than asserted — see ShareholderMatches. It sits above
            the manual links because it is the answer to the question somebody
            opened this record asking: is this person a customer, do they run a
            guide company, are they in the book. */}
        {form.matches && (
          <section className="recform__group">
            <h4 className="recform__legend">Elsewhere in Skida</h4>
            <ul className="elsewhere">
              {form.matches.proMember && (
                <li>
                  <span className="elsewhere__tag elsewhere__tag--pro">Skida PRO</span>
                  {form.matches.proMember.role} at <strong>{form.matches.proMember.companyName}</strong>
                </li>
              )}
              {form.matches.appUser && (
                <li>
                  <span className="elsewhere__tag">Skida app</span>
                  {form.matches.appUser.entitlements.length > 0 ? (
                    <>
                      paying — {form.matches.appUser.entitlements.join(', ')}
                    </>
                  ) : (
                    'has an account, nothing paid'
                  )}
                </li>
              )}
              {form.matches.ambassador && (
                <li>
                  <span className="elsewhere__tag">Ambassador</span>
                  {form.matches.ambassador.name} · {form.matches.ambassador.status}
                </li>
              )}
              {form.matches.partner && (
                <li>
                  <span className="elsewhere__tag">Partner</span>
                  {form.matches.partner.name}
                </li>
              )}
              {form.matches.lead && (
                <li>
                  <span className="elsewhere__tag">Sales book</span>
                  {form.matches.lead.name} · {form.matches.lead.stage}
                </li>
              )}
            </ul>
            <p className="recform__note">
              Matched on the email address{form.matches.matchedAt ? ` on ${formatDate(form.matches.matchedAt.slice(0, 10))}` : ''}, not entered by hand. Names are
              deliberately not matched: a company that looks like a lead usually is one, and
              usually is not good enough here.
            </p>
          </section>
        )}

        {/* The aksjeeierbok holds more than a name and a holding — decision
            pending, see designfeedback. These come from the register kept for
            Foretaksregisteret and Folkeinvest: none of them is derived and none
            affects an arithmetic, which is why they sit apart from Details. */}
        <section className="recform__group">
          <h4 className="recform__legend">Register details</h4>
          <div className="recform__grid">
            <label className="field recform__wide">
              <span>Legal name (Foretaksregisteret)</span>
              <input
                className="input"
                value={form.legalName ?? ''}
                placeholder={form.name}
                onChange={(e) => set({ legalName: e.target.value })}
              />
            </label>
            {form.kind === 'company' && (
              <label className="field">
                <span>Contact person</span>
                <input
                  className="input"
                  value={form.contactPerson ?? ''}
                  onChange={(e) => set({ contactPerson: e.target.value })}
                />
              </label>
            )}
            <label className="field">
              <span>Alternative email</span>
              <input
                className="input"
                value={form.altEmail ?? ''}
                onChange={(e) => set({ altEmail: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Postcode</span>
              <input
                className="input"
                value={form.postalCode ?? ''}
                onChange={(e) => set({ postalCode: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Place</span>
              <input
                className="input"
                value={form.city ?? ''}
                onChange={(e) => set({ city: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Country</span>
              <input
                className="input"
                value={form.country ?? ''}
                onChange={(e) => set({ country: e.target.value })}
              />
            </label>
            {form.kind === 'person' && (
              <label className="field">
                <span>Born / place</span>
                <input
                  className="input"
                  value={form.birthNote ?? ''}
                  onChange={(e) => set({ birthNote: e.target.value })}
                />
              </label>
            )}
            <label className="field">
              <span>Share series</span>
              <input
                className="input own__mono"
                value={form.shareSeries ?? ''}
                placeholder="17106–30000"
                onChange={(e) => set({ shareSeries: e.target.value })}
              />
              {/* Decision 77: monospaced, with the note saying what it is. The
                  digits are a range of share numbers, and in a proportional face
                  `17106–30000` reads as a phone number — the figures do not line
                  up and the dash stops looking like a span. */}
              <span className="field__note">
                The share numbers this holding covers, as the register writes them.
              </span>
            </label>
            <label className="field">
              <span>On the register since</span>
              <input
                className="input"
                type="date"
                value={form.entryDate ?? ''}
                onChange={(e) => set({ entryDate: e.target.value })}
              />
            </label>
            <label className="field">
              <span>VPS account</span>
              <input
                className="input"
                value={form.vpsAccount ?? ''}
                onChange={(e) => set({ vpsAccount: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Folkeinvest reference</span>
              <input
                className="input"
                value={form.folkeinvestRef ?? ''}
                onChange={(e) => set({ folkeinvestRef: e.target.value })}
              />
            </label>
          </div>
          {/* Provenance. Read-only because it describes where the row came
              from, and editing it would make it describe something else. */}
          {(form.nameVariants || form.sources) && (
            <p className="recform__note">
              {form.nameVariants && (
                <>
                  Also appears as <strong>{form.nameVariants}</strong>.{' '}
                </>
              )}
              {form.sources && <>Assembled from {form.sources}.</>}
            </p>
          )}
        </section>

        {/* What else this holder is in the CRM. Which links are offered follows
            the kind, per decision 48: a company links to the lead itself, since
            it cannot be a contact on itself; a person links to a contact, an
            ambassador, or one of us — employees hold shares too. */}
        <section className="recform__group">
          <h4 className="recform__legend">Also in the CRM</h4>
          {form.kind === 'company' ? (
            <>
              <div className="recform__grid">
                <label className="field recform__wide">
                  <span>Lead</span>
                  <select
                    className="input"
                    value={form.linkedLeadId}
                    disabled={targetsLoading}
                    onChange={(e) => {
                      const id = e.target.value
                      const hit = targets.find((x) => x.id === id)
                      set({ linkedLeadId: id, linkedLabel: hit?.label ?? '' })
                    }}
                  >
                    <option value="">{targetsLoading ? 'Loading…' : 'Not linked'}</option>
                    {targets.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              {/* Shown for recognition, not as the link: the shareholder is the
                  company, and its contact is how somebody recognises which one. */}
              {primaryContact && (
                <p className="recform__note">Primary contact there: {primaryContact}.</p>
              )}
            </>
          ) : (
            <div className="recform__grid">
              <label className="field">
                <span>Kind</span>
                <select
                  className="input"
                  value={personLink}
                  onChange={(e) => {
                    setPersonLink(e.target.value as LinkKind)
                    // Switching clears what no longer applies, so a record cannot
                    // carry two links that disagree about who somebody is.
                    set({
                      linkedLeadContactId: '',
                      linkedLeadContact: '',
                      linkedAmbassadorId: '',
                      linkedTeamEmail: '',
                      linkedLabel: '',
                    })
                  }}
                >
                  <option value="none">Not linked</option>
                  <option value="contact">Lead contact</option>
                  <option value="ambassador">Ambassador</option>
                  <option value="team">Team member</option>
                </select>
              </label>
              {personLink !== 'none' && (
                <label className="field">
                  <span>Which one</span>
                  <select
                    className="input"
                    value={
                      personLink === 'contact'
                        ? form.linkedLeadContactId
                        : personLink === 'ambassador'
                          ? form.linkedAmbassadorId
                          : form.linkedTeamEmail
                    }
                    disabled={targetsLoading}
                    onChange={(e) => {
                      const id = e.target.value
                      const hit = targets.find((x) => x.id === id)
                      const label = hit?.label ?? ''
                      set(
                        personLink === 'contact'
                          ? {
                              linkedLeadContactId: id,
                              linkedLeadContact: label,
                              // A contact belongs to a lead, and the link keeps
                              // both so the tag can appear on the company too.
                              linkedLeadId: hit?.leadId ?? '',
                              linkedLabel: label,
                            }
                          : personLink === 'ambassador'
                            ? { linkedAmbassadorId: id, linkedLabel: label }
                            : { linkedTeamEmail: id, linkedLabel: label },
                      )
                    }}
                  >
                    <option value="">{targetsLoading ? 'Loading…' : 'Select…'}</option>
                    {targets.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}
          <p className="recform__note">
            Shown on that record as well — to admins only, since the fact that somebody is a
            shareholder is as admin-only as the register itself.
          </p>
        </section>

        <section className="recform__group">
          {/* Held, not issued: after a sale the two differ, and the number a
              register is read for is the one somebody holds today. The rows
              below add up to the other one, and the transfer list explains the
              gap. */}
          <h4 className="recform__legend">Shares — {nb(held)} held</h4>
          {/* Status follows the holdings and cannot be set by hand: shares make
              a shareholder, options alone an option holder, neither a potential
              investor. Said here because the pill is the first thing read and
              the reason for it is the last thing found. */}
          <p className="recform__note">
            Status is{' '}
            <span className="pill" style={{ background: status.bg, color: status.color }}>
              {status.label}
            </span>{' '}
            — it follows the holdings below.
          </p>
          {form.purchases.length > 0 && (
            <ul className="holding">
              {form.purchases.map((p, i) => (
                <li className="holding__row" key={`${p.issuanceId}-${i}`}>
                  <span>
                    <strong>{nb(p.shares)}</strong> shares — {issuanceName(p.issuanceId)}
                    {p.date ? ` · ${formatDate(p.date)}` : ''}
                    {/* Only when it differs from the round's. A price repeated on
                        every row would bury the three that are actually agreed. */}
                    {p.pricePerShare != null && (
                      <em className="holding__price">
                        {' '}
                        · {fmtKrPrice(p.pricePerShare)}/share, agreed
                      </em>
                    )}
                  </span>
                  {/*
                    Whether the money arrived — added 28 Aug 2026.

                    The shares exist either way: a purchase counts towards the
                    holding, the percentage and the outstanding total from the
                    day it is recorded, and paying does not change that. This is
                    the company's side of it, and the reason chasing Emisjon 4
                    meant reading a bank statement beside the screen.

                    Two states and one control, like the option row above: an
                    unpaid row offers the button, a paid one states the date and
                    can be undone. No confirm on either — decision 6, and a
                    mis-click costs one more click.
                  */}
                  {p.paidAt ? (
                    <span className="holding__state holding__state--paid">
                      Paid
                      {/*
                        The date is editable, not a label — 29 Aug 2026.
                        `Mark paid` stamps today, which is right for money that
                        arrived today and wrong for everything else. Marking the
                        three closed rounds put today's date on payments made in
                        2024, and correcting 83 of them took a script. A date
                        input is the difference between a button that records a
                        payment and one that records the day you pressed it.
                      */}
                      <input
                        className="input holding__paidon"
                        type="date"
                        value={p.paidAt}
                        onChange={(e) =>
                          set({
                            purchases: form.purchases.map((x, n) =>
                              n === i ? { ...x, paidAt: e.target.value } : x,
                            ),
                          })
                        }
                      />
                      <button
                        className="own__act"
                        onClick={() =>
                          set({
                            purchases: form.purchases.map((x, n) =>
                              n === i ? { ...x, paidAt: '' } : x,
                            ),
                          })
                        }
                      >
                        Undo
                      </button>
                    </span>
                  ) : (
                    <button
                      className="own__act own__act--pay"
                      onClick={() =>
                        set({
                          purchases: form.purchases.map((x, n) =>
                            n === i ? { ...x, paidAt: today() } : x,
                          ),
                        })
                      }
                    >
                      Mark paid
                    </button>
                  )}
                  {/*
                    The kvittering for this payment — 2 Sep 2026. Keyed the same
                    way `paidTransactions` keys it, and present only for a row
                    the saved record agrees is paid.
                  */}
                  {paid.has(`p-${p.issuanceId}-${i}`) && (
                    <button
                      className="own__act"
                      onClick={() => setOpenDoc({ kind: 'receipt', key: `p-${p.issuanceId}-${i}` })}
                    >
                      Receipt
                    </button>
                  )}
                  {/* The aksjeeierbevis, once this round has been reported to
                      Foretaksregisteret. Set the date on the round itself. The
                      button is English like the rest of the interface; the
                      document opens in Norwegian with a flag to switch. */}
                  {certifiable.has(`p-${p.issuanceId}-${i}`) && (
                    <button
                      className="own__act"
                      onClick={() =>
                        setOpenDoc({ kind: 'certificate', key: `p-${p.issuanceId}-${i}` })
                      }
                    >
                      Share certificate
                    </button>
                  )}
                  <button
                    className="holding__remove"
                    onClick={() => set({ purchases: form.purchases.filter((_, x) => x !== i) })}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="holding__add">
            <select
              className="input"
              value={buy.issuanceId}
              onChange={(e) => setBuy({ ...buy, issuanceId: e.target.value })}
            >
              <option value="">Round…</option>
              {issuances.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
            <input
              className="input"
              value={buy.shares}
              inputMode="numeric"
              placeholder="Shares"
              onChange={(e) => setBuy({ ...buy, shares: e.target.value })}
            />
            <input
              className="input"
              type="date"
              value={buy.date}
              onChange={(e) => setBuy({ ...buy, date: e.target.value })}
            />
            <input
              className="input"
              inputMode="decimal"
              value={buy.price}
              placeholder="Price, if agreed"
              title="Leave empty to use the round's own price"
              onChange={(e) => setBuy({ ...buy, price: e.target.value })}
            />
            <input
              className="input"
              value={buy.note}
              placeholder={buyIsInterest ? 'What the source recorded' : 'Note, if any'}
              title={
                buyIsInterest
                  ? 'Required for interest — the tegningsliste shows these words verbatim'
                  : 'Evidence for a price that differs from the round’s'
              }
              onChange={(e) => setBuy({ ...buy, note: e.target.value })}
            />
            <button className="btn btn--ghost" onClick={addPurchase}>
              {buyIsInterest ? 'Add interest' : 'Add'}
            </button>
          </div>
          {rowErrorFor('buy') && <p className="recform__error">{rowErrorFor('buy')}</p>}
          {/* Which ledger this row is about to go into, before it goes there.
              The form said the rule correctly and then did the opposite of it
              (F1); saying it beside the button is what makes the rule visible at
              the moment somebody is relying on it. */}
          {buyIsInterest && (
            <p className="recform__note own__interestnote">
              <strong>{buyRound?.name} is still open</strong>, so this is recorded as interest, not
              as a purchase: it stays out of the holding above, out of shares outstanding and out of
              every percentage until the round closes and the money arrives.
            </p>
          )}
          {issuances.length === 0 && (
            <p className="recform__note">No rounds yet — add one under Issuances first.</p>
          )}
        </section>

        {mine.length > 0 && (
          <section className="recform__group">
            <h4 className="recform__legend">Transfers</h4>
            <ul className="holding">
              {mine.map((t) => {
                const sold = t.fromId === holder.id
                return (
                  <li className="holding__row" key={t.id}>
                    <span>
                      <strong className={sold ? 'own__out' : 'own__in'}>
                        {sold ? '−' : '+'}
                        {nb(t.shares)}
                      </strong>{' '}
                      {/* A buy-back is the company buying its own shares, and the
                          table says exactly that. This used to read "sold to Off
                          the register", which describes the shares' fate from the
                          seller's side and reads as an error. */}
                      —{' '}
                      {t.buyback
                        ? // A5.5 — the same word the table and the board use.
                          'Buy-back'
                        : sold
                          ? `sold to ${holderName(t.toId)}`
                          : `bought from ${holderName(t.fromId)}`}
                      {t.date ? ` · ${formatDate(t.date)}` : ''}
                    </span>
                    {/* A pending row is shown, greyed, rather than hidden: it is
                        the reason the held figure is not what somebody expects. */}
                    <span className="holding__state">
                      {t.settled ? 'Settled' : 'Pending — not counted yet'}
                    </span>
                  </li>
                )
              })}
            </ul>
            <p className="recform__note">
              Edited under Transfers, not here — a transfer has two sides and belongs to neither
              record.
            </p>
          </section>
        )}

        {/* What we have actually sent this person. Not a communication log —
            nothing here is typed by hand — but the record of what left the
            building with their name and their numbers in it. Delivery state
            only: whether they opened it is not recorded anywhere per person. */}
        {mail.length > 0 && (
          <section className="recform__group">
            <h4 className="recform__legend">Sent to them</h4>
            <ul className="holding">
              {mail.map((m) => {
                const st = MAIL_STATES[m.status]
                return (
                  <li className="holding__row" key={m.id}>
                    <span>
                      <strong>{m.subject}</strong>
                      <span className="holding__state own__mailmeta">
                        {m.sentAt ? formatDate(m.sentAt) : 'undated'} · {m.email}
                        {m.amount > 0 ? ` · ${fmtKrExact(m.amount)}` : ''}
                        {m.failure ? ` · ${m.failure}` : ''}
                        {/* Opened is a ceiling, not a count — a mail client can
                            fetch the pixel without anybody reading. Said here
                            rather than left for somebody to assume. */}
                        {m.openedAt
                          ? ` · opened ${formatDate(m.openedAt)}${m.clicked ? ', clicked' : ''}`
                          : m.status === 'delivered'
                            ? ' · not opened yet'
                            : ''}
                      </span>
                    </span>
                    <span className={`own__mail own__mail--${st.tone}`}>{st.label}</span>
                  </li>
                )
              })}
            </ul>
          </section>
        )}

        {(form.subscriptions ?? []).length > 0 && (
          <section className="recform__group">
            <h4 className="recform__legend">Interest in open rounds</h4>
            <ul className="holding__list">
              {(form.subscriptions ?? []).map((sub, i) => {
                const round = issuances.find((x) => x.id === sub.issuanceId)
                return (
                  <li className="holding__row" key={`${sub.issuanceId}-${i}`}>
                    <span>
                      <strong>{nb(sub.shares)}</strong> in {round?.name ?? 'an unknown round'}
                      {sub.pricePerShare != null
                        ? ` at ${fmtKrPrice(sub.pricePerShare)}/share, agreed`
                        : ''}
                      {sub.note ? ` — ${sub.note}` : ''}
                    </span>
                    <span className="holding__state">Not issued — counted nowhere</span>
                    {/* Removable now that the form can add one. Interest is the
                        one row on this record that is safe to take back: it
                        counts towards nothing, so removing it changes no figure
                        anywhere — unlike a purchase, which is a share the
                        company issued and has to be transferred or bought back
                        rather than deleted (12). */}
                    <button
                      className="btn btn--ghost btn--sm"
                      onClick={() =>
                        set({
                          subscriptions: (form.subscriptions ?? []).filter((_, x) => x !== i),
                        })
                      }
                    >
                      Remove
                    </button>
                  </li>
                )
              })}
            </ul>
            <p className="recform__note">
              A subscription is a promise. It is not in the holding above, not in shares
              outstanding and not in any percentage, and it becomes a purchase when the round
              closes and the money arrives.
            </p>
          </section>
        )}

        <section className="recform__group">
          <h4 className="recform__legend">Options — {nb(activeOptions(form))} active</h4>
          <p className="recform__note own__optsum">
            {nb(grants.active)} active · {nb(grants.exercised)} exercised into shares ·{' '}
            {nb(grants.unpaid)} exercised awaiting payment · {nb(grants.expired)} expired
          </p>
          {form.options.length > 0 && (
            <ul className="holding">
              {form.options.map((o, i) => {
                const st = optionStateLabel(o)
                const unpaid = isUnpaidExercise(o)
                // Book, Let expire and the rest only change the draft, and a
                // refresh after Book silently lost a partial exercise. Saying so
                // on the row is cheaper than making Cancel mean less.
                const unsaved = !isNew && JSON.stringify(o) !== JSON.stringify(holder.options[i])
                return (
                  <li className="holding__row holding__row--wide" key={`${o.grantDate}-${i}`}>
                    <div className="holding__line">
                      <span>
                        <strong>{nb(o.count)}</strong> options
                        {o.strikePrice === null ? '' : ` — strike ${fmtKrPrice(o.strikePrice)}`}
                        {o.grantDate ? ` · granted ${formatDate(o.grantDate)}` : ''}
                        {o.expiryDate ? ` · expires ${formatDate(o.expiryDate)}` : ''}
                      </span>
                      <span className="pill" style={{ background: st.bg, color: st.color }}>
                        {st.label}
                      </span>
                    </div>
                    {unsaved && (
                      <p className="holding__unsaved">Not saved yet — press Save below.</p>
                    )}

                    {/* Decision 80. The 2 191 inherited grants carry a count and
                        nothing else, and the row has to name which terms are
                        missing rather than showing kr 0,00 and an empty date —
                        a nought strike is a price somebody agreed, and a blank
                        date field reads as a value that failed to load. Dashed,
                        in the not-final treatment (36). The row looking
                        unfinished is correct: it is. */}
                    {grantTermsNote(o) && (
                      <p className="holding__gap">{grantTermsNote(o)}</p>
                    )}
                    {/* The exercise's own paperwork, where it is missing — same
                        device as 80's missing terms, and for the same reason.
                        With the field to fix it: a note telling somebody to add
                        a resolution, on a screen with nowhere to type one, is
                        worse than no note. The booking form asks for it, but
                        only while a grant is still unexercised. */}
                    {exerciseGapNote(o) && (
                      <div className="holding__gap">
                        <p className="holding__gaptext">{exerciseGapNote(o)}</p>
                        <div className="holding__gapfix">
                          <input
                            className="input"
                            value={o.exerciseResolution}
                            placeholder="Board / AGM resolution"
                            onChange={(e) =>
                              set({
                                options: form.options.map((x, n) =>
                                  n === i ? { ...x, exerciseResolution: e.target.value } : x,
                                ),
                              })
                            }
                          />
                          {!o.exerciseIssuanceId && (
                            <select
                              className="input"
                              value={o.exerciseIssuanceId}
                              onChange={(e) =>
                                set({
                                  options: form.options.map((x, n) =>
                                    n === i ? { ...x, exerciseIssuanceId: e.target.value } : x,
                                  ),
                                })
                              }
                            >
                              <option value="">Booked against…</option>
                              {issuances.map((x) => (
                                <option key={x.id} value={x.id}>
                                  {x.name}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Booking an exercise is a small form, not a button, because
                        it is a capital increase: it has to say which increase,
                        under what resolution, and on what date. */}
                    {booking === i ? (
                      <div className="own__book">
                        <div className="holding__add holding__add--book">
                          <select
                            className="input"
                            value={book.issuanceId}
                            onChange={(e) => setBook({ ...book, issuanceId: e.target.value })}
                          >
                            <option value="">Book against…</option>
                            {issuances.map((x) => (
                              <option key={x.id} value={x.id}>
                                {x.name}
                              </option>
                            ))}
                            <option value={NEW_INCREASE}>
                              + New increase at kr {o.strikePrice === null ? '—' : nb(o.strikePrice)}
                            </option>
                          </select>
                          <input
                            className="input"
                            value={book.resolution}
                            placeholder="Board / AGM resolution"
                            onChange={(e) => setBook({ ...book, resolution: e.target.value })}
                          />
                          <input
                            className="input"
                            type="date"
                            title="Exercise date"
                            value={book.date}
                            onChange={(e) => setBook({ ...book, date: e.target.value })}
                          />
                          <label className="own__bookcount" title="Options exercised">
                            <input
                              className="input"
                              type="number"
                              min={1}
                              max={o.count}
                              step={1}
                              aria-label="Options exercised"
                              value={book.count}
                              onChange={(e) => setBook({ ...book, count: e.target.value })}
                            />
                            <span>of {nb(o.count)}</span>
                          </label>
                          <button className="own__act" onClick={() => bookExercise(i)}>
                            Book
                          </button>
                        </div>
                        {/* F2: here, not at the foot of the dialog. */}
                        {rowErrorFor('book') && (
                          <p className="recform__error">{rowErrorFor('book')}</p>
                        )}
                        <p className="recform__note">
                          The shares are counted once the strike price is paid, not now.
                          {(() => {
                            const n = Number(book.count)
                            return Number.isInteger(n) && n >= 1 && n < o.count
                              ? ` The other ${nb(o.count - n)} stay active as their own grant, on the same terms.`
                              : ''
                          })()}
                          {book.issuanceId === NEW_INCREASE &&
                            ' The increase is created when this record is saved.'}
                        </p>
                      </div>
                    ) : (
                      <div className="holding__acts">
                        {o.status === 'active' ? (
                          <>
                            <button
                              className="own__act"
                              onClick={() => {
                                setBooking(i)
                                setBook({
                                  issuanceId: '',
                                  resolution: '',
                                  date: today(),
                                  count: String(o.count),
                                })
                              }}
                            >
                              Exercise into shares
                            </button>
                            <button className="own__act" onClick={() => letExpire(i)}>
                              Let expire
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="holding__state">
                              {o.status === 'expired'
                                ? `Lapsed ${o.expiredDate ? formatDate(o.expiredDate) : ''} — never became shares`
                                : `Exercised ${o.exerciseDate ? formatDate(o.exerciseDate) : ''} · ${issuanceName(o.exerciseIssuanceId)}${o.exerciseResolution ? ` · ${o.exerciseResolution}` : ''} — ${
                                    unpaid
                                      ? 'awaiting the strike price, not counted as shares'
                                      : `paid ${formatDate(o.paidDate)}, counted as shares above`
                                  }`}
                            </span>
                            {unpaid && (
                              <button
                                className="own__act own__act--pay"
                                onClick={() => patchGrant(i, { paidDate: today() })}
                              >
                                Strike price paid
                              </button>
                            )}
                            {/* An exercise that was paid for is a payment of
                                share capital like any other, so it gets the
                                same kvittering. */}
                            {paid.has(`o-${o.grantDate}-${i}`) && (
                              <button
                                className="own__act"
                                onClick={() =>
                                  setOpenDoc({ kind: 'receipt', key: `o-${o.grantDate}-${i}` })
                                }
                              >
                                Receipt
                              </button>
                            )}
                            {certifiable.has(`o-${o.grantDate}-${i}`) && (
                              <button
                                className="own__act"
                                onClick={() =>
                                  setOpenDoc({ kind: 'certificate', key: `o-${o.grantDate}-${i}` })
                                }
                              >
                                Share certificate
                              </button>
                            )}
                            <button className="own__act" onClick={() => reactivate(i)}>
                              Undo
                            </button>
                          </>
                        )}
                        <button
                          className="holding__remove"
                          onClick={() => set({ options: form.options.filter((_, x) => x !== i) })}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
          <div className="holding__add holding__add--options">
            <input
              className="input"
              value={grant.count}
              inputMode="numeric"
              placeholder="Count"
              onChange={(e) => setGrant({ ...grant, count: e.target.value })}
            />
            <input
              className="input"
              value={grant.strikePrice}
              inputMode="decimal"
              placeholder="Strike (kr), e.g. 9,50"
              onChange={(e) => setGrant({ ...grant, strikePrice: e.target.value })}
            />
            <input
              className="input"
              type="date"
              title="Grant date"
              value={grant.grantDate}
              onChange={(e) => setGrant({ ...grant, grantDate: e.target.value })}
            />
            <input
              className="input"
              type="date"
              title="Expiry date"
              value={grant.expiryDate}
              onChange={(e) => setGrant({ ...grant, expiryDate: e.target.value })}
            />
            <button className="btn btn--ghost" onClick={addGrant}>
              Add
            </button>
          </div>
          {rowErrorFor('grant') && <p className="recform__error">{rowErrorFor('grant')}</p>}
        </section>

        <AttachmentsField
          scope="shareholders"
          recordId={isNew ? undefined : holder.id}
          attachments={attachments}
          onChange={setAttachments}
        />

        {error && <p className="recform__error">{error}</p>}
      </div>

      {/* Portalled to <body>, so Print produces the document and not the dialog
          it was opened from. */}
      {openTx && openDoc?.kind === 'receipt' && (
        <ShareReceipt
          receipt={buildReceipt(
            holder,
            openTx,
            company,
            issuances.find((x) => x.id === openTx.issuanceId),
          )}
          issuedOn={today()}
          onClose={() => setOpenDoc(null)}
        />
      )}
      {openTx && openDoc?.kind === 'certificate' && (
        <ShareCertificate
          certificate={buildCertificate(
            holder,
            openTx,
            company,
            issuances.find((x) => x.id === openTx.issuanceId),
            /*
              Only the shares Foretaksregisteret has been told about — 17 Sep
              2026. `netShares` counts every round, reported or not, which on a
              document of title is a claim about shares the public register does
              not yet know exist. The saved register, not the draft, and as at
              today.
            */
            {
              shares: registeredShares(
                holder,
                issuances,
                transfers
                  .filter((t) => t.fromId === holder.id || t.toId === holder.id)
                  .map((t) => ({
                    shares: t.shares,
                    direction: t.toId === holder.id ? ('in' as const) : ('out' as const),
                    settled: t.settled,
                  })),
              ),
              asOf: today(),
            },
          )}
          issuedOn={today()}
          onClose={() => setOpenDoc(null)}
        />
      )}
    </Modal>
  )
}

export const emptyShareholder = (): Shareholder => ({ id: '', ...EMPTY_SHAREHOLDER })
